import { useState } from 'react'

import { MessagePopup } from '@skytech/ui'
import { getWhiteLabelForDealerStyle } from '@skytech/utils'
import CancelIcon from 'mdi-react/CancelIcon'
import CheckIcon from 'mdi-react/CheckIcon'
import PlayCircleOutlineIcon from 'mdi-react/PlayCircleOutlineIcon'
import PlusIcon from 'mdi-react/PlusIcon'
import { useDispatch, useSelector } from 'react-redux'

import { useAppContext } from '@/App.context'
import Button from '@/components/form/Button'
import { Translation } from '@/components/i18n/Translation'
import { activatePopup } from '@/ducks/popupReducer'
import {
  cancelSubscriptionOrder,
  completeSubscriptionOrder,
  getIsPostPending,
  getSubscriptionOrder,
  startSubscriptionOrder,
  terminateSubscriptionOrder,
} from '@/ducks/subscriptionOrderReducer'
import { isOrderOfType, SUBSCRIPTION_ORDER_STATUS, SUBSCRIPTION_ORDER_TYPES } from '@/util/orders'

import CreateCostObjectForm from './CreateCostObjectForm'

import './subscription-order-options.scss'

const SubscriptionOrderOptions = () => (
  <div className="subscription-order__options">
    {/* @ts-ignore */}
    <StartOrder />
    {/* @ts-ignore */}
    <CompleteOrder />
    {/* @ts-ignore */}
    <TerminateOrder />
    {/* @ts-ignore */}
    <CancelOrder />
    <CreateCostObject />
  </div>
)

const CancelOrder = () => {
  const dispatch = useDispatch()

  const { status, id, ogStatus } = useSelector(getSubscriptionOrder)
  const isPostPending = useSelector(getIsPostPending)

  // [Done, Canceled, Terminated]
  if ([2, 5, 4].includes(ogStatus)) return null

  return (
    [
      SUBSCRIPTION_ORDER_STATUS.WAITING_FOR_USER,
      SUBSCRIPTION_ORDER_STATUS.WAITING_FOR_DEALER,
      SUBSCRIPTION_ORDER_STATUS.PROCESSING,
      SUBSCRIPTION_ORDER_STATUS.TERMINATION_REQUEST,
      SUBSCRIPTION_ORDER_STATUS.FOR_MANUAL_HANDLING,
      SUBSCRIPTION_ORDER_STATUS.FAILED,
    ].includes(status) && (
      <div className="order-options__option">
        <Button
          Icon={CancelIcon}
          disabled={isPostPending}
          // @ts-ignore
          onClick={() => dispatch(cancelSubscriptionOrder(id))}
        >
          <Translation id="Cancel Order" />
        </Button>
      </div>
    )
  )
}

const StartOrder = () => {
  const dispatch = useDispatch()

  const { status, id, ogStatus } = useSelector(getSubscriptionOrder)
  const isPostPending = useSelector(getIsPostPending)

  // NEW || FAILED
  if (ogStatus !== 0 && ogStatus !== 9) return null

  return (
    [
      SUBSCRIPTION_ORDER_STATUS.WAITING_FOR_USER,
      SUBSCRIPTION_ORDER_STATUS.WAITING_FOR_DEALER,
      SUBSCRIPTION_ORDER_STATUS.FOR_MANUAL_HANDLING,
      SUBSCRIPTION_ORDER_STATUS.FAILED,
    ].includes(status) && (
      <div className="order-options__option">
        <Button
          Icon={PlayCircleOutlineIcon}
          disabled={isPostPending}
          // @ts-ignore
          onClick={() => dispatch(startSubscriptionOrder(id))}
        >
          <Translation id="Order Started" />
        </Button>
      </div>
    )
  )
}

const CompleteOrder = () => {
  const { whiteLabel } = useAppContext()
  const dispatch = useDispatch()

  const [isErrorPopupOpen, setIsErrorPopupOpen] = useState(false)

  const { status, id, code, ogStatus } = useSelector(getSubscriptionOrder)
  const isPostPending = useSelector(getIsPostPending)

  function handleCompleteOrder() {
    if (!code) {
      setIsErrorPopupOpen(true)
      return
    }
    // @ts-ignore
    dispatch(completeSubscriptionOrder(id))
  }

  // PROCESSING
  if (ogStatus !== 1) return null

  return (
    [SUBSCRIPTION_ORDER_STATUS.PROCESSING, SUBSCRIPTION_ORDER_STATUS.FOR_MANUAL_HANDLING].includes(
      status
    ) && (
      <div className="order-options__option">
        <Button Icon={CheckIcon} disabled={isPostPending} onClick={handleCompleteOrder}>
          <Translation id="Order Completed" />
        </Button>

        <MessagePopup
          title={<Translation id="Setting order as delivered error" />}
          message={
            <Translation id="It is unfortunately not possible to set an order as delivered without providing contact number." />
          }
          isOpen={isErrorPopupOpen}
          onClose={() => setIsErrorPopupOpen(false)}
          whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
        />
      </div>
    )
  )
}

const TerminateOrder = () => {
  const dispatch = useDispatch()

  const { status, id, ogStatus } = useSelector(getSubscriptionOrder)
  const isPostPending = useSelector(getIsPostPending)

  // TERMINATION_REQUEST
  if (ogStatus !== 3) return null

  return (
    [
      SUBSCRIPTION_ORDER_STATUS.TERMINATION_REQUEST,
      SUBSCRIPTION_ORDER_STATUS.FOR_MANUAL_HANDLING,
    ].includes(status) && (
      <div className="order-options__option">
        <Button
          Icon={CheckIcon}
          disabled={isPostPending}
          // @ts-ignore
          onClick={() => dispatch(terminateSubscriptionOrder(id))}
        >
          <Translation id="Set to downgraded" />
        </Button>
      </div>
    )
  )
}

const CreateCostObject = () => {
  const dispatch = useDispatch()

  const order = useSelector(getSubscriptionOrder)
  const { cost_object_created, code } = order

  if (!(isOrderOfType(order, SUBSCRIPTION_ORDER_TYPES.PORTING) && !cost_object_created && code)) {
    return null
  }

  return (
    <div className="order-options__option">
      <Button Icon={PlusIcon} onClick={() => dispatch(activatePopup(<CreateCostObjectForm />))}>
        <Translation id="Create Cost Object" />
      </Button>
    </div>
  )
}

export default SubscriptionOrderOptions
